import {
    ChangeEvent,
    FocusEvent,
    FunctionComponent,
    useCallback,
    useEffect,
    useState,
} from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import {
    NumberInput,
    NumberInputProps,
} from '@/components/NumberInput/NumberInput';
import {
    MoneyInput,
    MoneyInputProps,
} from '@/components/MoneyInput/MoneyInput';
import { Money } from '@/models/money';
import { useFormErrorContext } from '@/context/FormErrorContext';

type TextFieldWrapperProps = Omit<TextFieldProps, 'onChange' | 'onBlur'> & {
    onBlur?: (nextValue: string, e: FocusEvent<HTMLInputElement>) => void;
    onChange?: (nextValue: string, e: ChangeEvent<HTMLInputElement>) => void;
};

export const TextFieldWrapper: FunctionComponent<TextFieldWrapperProps> = ({
    value,
    ...rest
}) => {
    const [internalValue, setInternalValue] = useState(value);
    const [errors, setError] = useFormErrorContext();

    const error = errors[rest.name as string];

    const onInputChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const { value } = e.target;

            setInternalValue(value);
            rest.onChange?.(value, e);
        },
        [setInternalValue]
    );

    const onInputBlur = useCallback(
        (e: FocusEvent<HTMLInputElement>) => {
            const { value } = e.target;

            if (value) {
                setError(rest.name, null);
            }

            rest.onBlur?.(value, e);
        },
        [setError]
    );

    useEffect(() => {
        setInternalValue(value);
    }, [value]);

    return (
        <TextField
            {...rest}
            variant='filled'
            autoComplete='off'
            onChange={onInputChange}
            value={internalValue}
            onBlur={onInputBlur}
            error={!!error}
            helperText={error || ''}
        />
    );
};

type NumberInputFieldWrapperProps = Omit<
    NumberInputProps,
    'onChange' | 'onBlur'
> & {
    onBlur: (nextValue: number, e: FocusEvent<HTMLInputElement>) => void;
};

export const NumberInputFieldWrapper: FunctionComponent<
    NumberInputFieldWrapperProps
> = ({ value, ...rest }) => {
    const [internalValue, setInternalValue] = useState<number>(value ?? null);

    const onInputChange = useCallback(
        (nextVal: number) => {
            setInternalValue(nextVal ?? null);
        },
        [setInternalValue]
    );

    const onInputBlur = useCallback(
        (e: FocusEvent<HTMLInputElement>) => {
            rest?.onBlur(internalValue, e);
        },
        [internalValue]
    );

    useEffect(() => {
        setInternalValue(value ?? null);
    }, [value]);

    return (
        <NumberInput
            {...rest}
            onChange={onInputChange}
            value={internalValue}
            onBlur={onInputBlur}
        />
    );
};

type MoneyInputFieldWrapperProps = Omit<
    MoneyInputProps,
    'onChange' | 'onBlur'
> & {
    onBlur: (nextValue: Money, e: FocusEvent<HTMLInputElement>) => void;
};

export const MoneyInputFieldWrapper: FunctionComponent<
    MoneyInputFieldWrapperProps
> = ({ value, ...rest }) => {
    const [internalValue, setInternalValue] = useState<Money>(value);

    const onInputChange = useCallback(
        (nextVal: Money) => {
            setInternalValue(nextVal);
        },
        [setInternalValue]
    );

    const onInputBlur = useCallback(
        (e: FocusEvent<HTMLInputElement>) => {
            rest?.onBlur(internalValue, e);
        },
        [internalValue]
    );

    useEffect(() => {
        setInternalValue(value ?? null);
    }, [value]);

    return (
        <MoneyInput
            {...rest}
            onChange={onInputChange}
            value={internalValue ?? null}
            onBlur={onInputBlur}
        />
    );
};
